import { MouseEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { alpha } from '@mui/material/styles';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowHeightParams,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { handymanActionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { DASHBOARD_GRID_ROW_HEIGHT } from 'src/common/components/dataGrid/useDashboardGridHeight';
import { IEnrichedParticipant, IParticipant, ParticipantsTableLayout } from 'src/interfaces/Participant';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

import {
  ParticipantCellCallbacks,
  PARTICIPANT_DETAIL_ROW_EXTRA_HEIGHT,
  PARTICIPANT_DOB_WARNING_ROW_EXTRA_HEIGHT,
  ParticipantCompetitionCell,
  needsDateOfBirthWarning,
  ParticipantDistanceCell,
  ParticipantEntryCell,
  ParticipantExpandToggle,
  ParticipantNameWithDetailsCell,
  ParticipantPrimaryRowBand,
  ParticipantPriceCell,
  ParticipantRowActionsMenu,
  ParticipantSerCell,
  ParticipantQrCell,
  ParticipantStatusCell,
  isParticipantRowClickable,
} from './participantDataGridCells';

;

interface Props {
  checkin?(id?: string, e?: MouseEvent<unknown>): void;
  register?(id?: string, e?: MouseEvent<unknown>): void;
  editRegistration?(id?: string, e?: MouseEvent<unknown>): void;
  deleteRegistration?(id?: string, e?: MouseEvent<unknown>): void;
  participants: IParticipant[];
  deleteParticipant(id: string, e?: MouseEvent<unknown>): void;
  editParticipant(id?: string, e?: MouseEvent<unknown>): void;
  transferParticipant?(id: string, e?: MouseEvent<unknown>): void;
  order?: unknown;
  orderByKeys?: string;
  sort?: unknown;
  onDeleteCheckin?: (checkinId: string, e?: MouseEvent<unknown>) => void;
  registrationIds?: Record<string, string>;
  layout?: ParticipantsTableLayout;
  tourRegisterAnchorParticipantId?: string;
  tourQrAnchorParticipantId?: string;
}

const ParticipantsTable = ({
  checkin,
  register,
  editRegistration,
  deleteRegistration,
  participants,
  deleteParticipant,
  editParticipant,
  transferParticipant,
  registrationIds,
  layout = 'admin',
  tourRegisterAnchorParticipantId,
  tourQrAnchorParticipantId,
}: Props) => {
  const { t } = useTranslation();
  const isMyLayout = layout === 'my';
  const { activeEditionId, editionsById } = useSystemOptionsStore(systemOptionsState => systemOptionsState);
  const registerForEditionTooltip =
    activeEditionId && editionsById?.[activeEditionId]
      ? t('registrations.registerForEdition', {
          eventName: `${t('base.defaultTitle')} ${editionsById[activeEditionId].year}`,
        })
      : t('registrations.registerForEvent');

  const rows = participants as IEnrichedParticipant[];
  const apiRef = useGridApiRef();
  const [expandedRowIds, setExpandedRowIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      apiRef.current?.resetRowHeights();
    });
    return () => cancelAnimationFrame(frame);
  }, [apiRef, expandedRowIds]);

  const toggleRowExpanded = useCallback((rowId: string) => {
    setExpandedRowIds(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  const isRowExpanded = useCallback(
    (rowId: string) => !!expandedRowIds[rowId],
    [expandedRowIds],
  );

  const wrapPrimaryBand = useCallback(
    (rowId: string, content: ReactNode) => (
      <ParticipantPrimaryRowBand isExpanded={isRowExpanded(rowId)}>{content}</ParticipantPrimaryRowBand>
    ),
    [isRowExpanded],
  );

  const cellCallbacks = useMemo(
    (): ParticipantCellCallbacks => ({
      checkin,
      register,
      editRegistration,
      deleteRegistration,
      registerTooltip: isMyLayout ? registerForEditionTooltip : undefined,
      tourRegisterAnchorParticipantId,
      tourQrAnchorParticipantId,
    }),
    [
      checkin,
      register,
      editRegistration,
      deleteRegistration,
      isMyLayout,
      registerForEditionTooltip,
      tourRegisterAnchorParticipantId,
      tourQrAnchorParticipantId,
    ],
  );

  const getRowHeight = useCallback(
    (params: GridRowHeightParams) => {
      const base = DASHBOARD_GRID_ROW_HEIGHT;
      let extra = 0;
      if (expandedRowIds[params.id as string]) {
        extra += PARTICIPANT_DETAIL_ROW_EXTRA_HEIGHT;
      }
      const rowCallbacks: ParticipantCellCallbacks = {
        ...cellCallbacks,
        registrationId: registrationIds?.[params.id as string],
      };
      if (needsDateOfBirthWarning(params.model as IEnrichedParticipant, rowCallbacks)) {
        extra += PARTICIPANT_DOB_WARNING_ROW_EXTRA_HEIGHT;
      }
      return base + extra;
    },
    [cellCallbacks, expandedRowIds, registrationIds],
  );

  const expandColumn = useMemo(
    (): GridColDef<IEnrichedParticipant> => ({
      field: '__expand__',
      headerName: '',
      width: 48,
      minWidth: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <ParticipantExpandToggle
          expanded={isRowExpanded(row.id)}
          isExpanded={isRowExpanded(row.id)}
          onToggle={() => toggleRowExpanded(row.id)}
        />
      ),
    }),
    [isRowExpanded, toggleRowExpanded],
  );

  const renderNameCell = useCallback(
    ({ row }: GridRenderCellParams<IEnrichedParticipant>) => (
      <ParticipantNameWithDetailsCell
        participant={row}
        callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
        showDetails={!!expandedRowIds[row.id]}
      />
    ),
    [cellCallbacks, expandedRowIds, registrationIds],
  );

  const columns = useMemo((): GridColDef<IEnrichedParticipant>[] => {
    const myCols: GridColDef<IEnrichedParticipant>[] = [
      expandColumn,
      {
        field: 'status',
        headerName: t('participants.statusColumn'),
        width: 128,
        minWidth: 128,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantStatusCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
      {
        field: 'qr',
        headerName: t('participants.qrColumn'),
        width: 56,
        minWidth: 56,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantQrCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
      {
        field: 'name',
        headerName: t('form.name'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: renderNameCell,
      },
      {
        field: 'phoneNumber',
        headerName: t('form.phoneNo'),
        width: 130,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(row.id, row.phoneNumber || '-'),
      },
      {
        field: 'entry',
        headerName: t('participants.entryColumn'),
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantEntryCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
    ];

    const adminCols: GridColDef<IEnrichedParticipant>[] = [
      expandColumn,
      {
        field: 'ser',
        headerName: t('form.ser'),
        width: 100,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantSerCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
      {
        field: 'status',
        headerName: t('participants.statusColumn'),
        width: 128,
        minWidth: 128,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantStatusCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
      {
        field: 'qr',
        headerName: t('participants.qrColumn'),
        width: 56,
        minWidth: 56,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantQrCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
      {
        field: 'name',
        headerName: t('form.name'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: renderNameCell,
      },
      {
        field: 'phoneNumber',
        headerName: t('form.phoneNo'),
        width: 130,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(row.id, row.phoneNumber || '-'),
      },
      {
        field: 'competition',
        headerName: t('form.event'),
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantCompetitionCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
      {
        field: 'distance',
        headerName: t('form.distance'),
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantDistanceCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
      {
        field: 'price',
        headerName: t('checkins.price'),
        width: 100,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(
            row.id,
            <ParticipantPriceCell
              participant={row}
              callbacks={{ ...cellCallbacks, registrationId: registrationIds?.[row.id] }}
            />,
          ),
      },
    ];

    const base = isMyLayout ? myCols : adminCols;

    base.push(
      handymanActionsColumn<IEnrichedParticipant>(t, 'participants.optionsLabel', ({ row }) =>
        wrapPrimaryBand(
          row.id,
          <ParticipantRowActionsMenu
            participantId={row.id}
            onTransfer={
              transferParticipant ? e => transferParticipant(row.id, e) : undefined
            }
            onEdit={e => editParticipant(row.id, e)}
            onDelete={e => deleteParticipant(row.id, e)}
          />,
        ),
      ),
    );

    return base;
  }, [
    cellCallbacks,
    deleteParticipant,
    editParticipant,
    expandColumn,
    isMyLayout,
    registrationIds,
    renderNameCell,
    t,
    transferParticipant,
    wrapPrimaryBand,
  ]);

  const getRowClassName = (params: GridRowClassNameParams<IEnrichedParticipant>) => {
    const classes: string[] = [];
    if (expandedRowIds[params.id as string]) {
      classes.push('participant-row-expanded');
    }
    const callbacks: ParticipantCellCallbacks = {
      ...cellCallbacks,
      registrationId: registrationIds?.[params.id as string],
    };
    if (isParticipantRowClickable(params.row, layout, callbacks)) {
      classes.push('participant-register-row');
    }
    return classes.join(' ');
  };

  const handleRowClick = (params: GridRowParams<IEnrichedParticipant>) => {
    const callbacks: ParticipantCellCallbacks = {
      ...cellCallbacks,
      registrationId: registrationIds?.[params.row.id],
    };
    if (isParticipantRowClickable(params.row, layout, callbacks)) {
      register?.(params.row.id);
    }
  };

  return (
    <DashboardDataGrid<IEnrichedParticipant>
      apiRef={apiRef}
      aria-label={t('participants.participantsTableLabel')}
      chrome="standard"
      rows={rows}
      columns={columns}
      getRowHeight={getRowHeight}
      onRowClick={handleRowClick}
      getRowClassName={getRowClassName}
      sx={{
        '& .participant-row-expanded .MuiDataGrid-cell': {
          alignItems: 'flex-start',
          overflow: 'hidden',
        },
        '& .participant-row-expanded .MuiDataGrid-cellContent': {
          height: '100%',
          overflow: 'hidden',
        },
        '& .participant-register-row': {
          cursor: 'pointer',
          bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
          '&:hover': {
            bgcolor: theme => alpha(theme.palette.warning.main, 0.18),
          },
        },
      }}
    />
  );
};

export default ParticipantsTable;
